import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { buildWhatsappUrl, openWhatsappChat } from '../utils/support';
import { supportCaseFor } from '../utils/supportCase';
import { APP } from '../config/app';
import useScrollLock from '../utils/useScrollLock';
import { createSupportCase } from '../services/firebase';

const ISSUE_CATEGORIES = [
  { id: 'fare', emoji: '💳', tk: 'support.catFare' },
  { id: 'lost', emoji: '🎒', tk: 'support.catLost' },
  { id: 'captain', emoji: '👤', tk: 'support.catCaptain' },
  { id: 'other', emoji: '❓', tk: 'support.catOther' },
];

export default function SupportModal({ open = true, onClose, userProfile, defaultRide = null, t = (k) => k }) {
  const [selectedCategory, setSelectedCategory] = useState('other');
  const [customNotes, setCustomNotes] = useState('');
  const [launching, setLaunching] = useState(false);
  useScrollLock(open);

  if (!open) return null;

  const selectedCatObj = ISSUE_CATEGORIES.find((c) => c.id === selectedCategory);

  const handleLaunchWhatsapp = () => {
    if (launching) return;
    setLaunching(true);
    const name = userProfile?.name || 'Passenger';
    const phone = userProfile?.phone || '';
    const city = APP.city || 'Pileru';

    let msg = `👋 Hello MyTownly Support Team,\n\nI need assistance with MyTownly Rides.\n\n👤 Passenger: ${name} (${phone})\n📍 City: ${city}\n📱 App Version: v${APP.version}\n⚠️ Issue Category: ${selectedCatObj?.id || 'general'}\n`;

    if (defaultRide) {
      const rideDate = defaultRide.createdAt?.toDate
        ? defaultRide.createdAt.toDate().toLocaleString()
        : 'Recent Ride';
      const pickupStr = defaultRide.pickup?.name || defaultRide.pickup?.address || 'Pickup';
      const dropStr = defaultRide.destination?.name || defaultRide.destination?.address || 'Drop';

      // An Intertown trip has no payment record, so its fare is not called "Cash".
      const intertown = defaultRide.kind === 'intertown';
      msg += `\n🛺 Ride Details:\n• ${intertown ? 'Intertown trip ID' : 'Ride ID'}: #${defaultRide.id?.slice(0, 8) || 'Trip'}\n• Date: ${rideDate}\n• Pickup: ${pickupStr}\n• Drop: ${dropStr}\n• Fare: ₹${defaultRide.fare || 0}${intertown ? '' : ' Cash'}\n`;
    }

    if (customNotes.trim()) {
      msg += `\nAdditional Notes: ${customNotes.trim()}\n`;
    }

    const openSupport = (caseId = '') => {
      const finalMsg = caseId
        ? `${msg}\n🆔 Support Case: ${caseId}\nStatus: Open\nPlease keep this reference for follow-up.\n\n📎 If relevant, attach screenshots or photos in this WhatsApp chat.`
        : `${msg}\n\n📎 If relevant, attach screenshots or photos in this WhatsApp chat.`;
      
      openWhatsappChat(finalMsg);
      if (caseId) toast.success(`${t('support.caseCreated') || 'Support case opened'}: ${caseId}`);
    };

    // 2s timeout on case creation so slow networks never block launching WhatsApp
    const casePromise = createSupportCase(supportCaseFor(defaultRide, selectedCatObj?.id || 'other', customNotes));
    const timeoutPromise = new Promise((resolve) => setTimeout(() => resolve(null), 2000));

    Promise.race([casePromise, timeoutPromise])
      .then((result) => openSupport(result?.caseId || ''))
      .catch(() => openSupport(''))
      .finally(() => {
        setLaunching(false);
        onClose();
      });
  };

  return (
    <div className="modal-backdrop support-modal-backdrop" onClick={onClose}>
      <div className="support-modal-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="support-modal-header">
          <h2>🎧 {t('support.title')}</h2>
          <button type="button" className="support-modal-close" onClick={onClose} aria-label={t('common.close')}>
            ✕
          </button>
        </div>

        <div className="support-modal-body">
          {defaultRide && (
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '12px', padding: '12px 14px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '20px' }}>🛺</span>
              <div style={{ flex: 1, fontSize: '12px', color: '#166534' }}>
                <strong>{t('support.forRide', { id: defaultRide.id?.slice(0, 8).toUpperCase() })}</strong>
                <div style={{ color: '#15803d', marginTop: '2px' }}>
                  {defaultRide.destination?.name || defaultRide.destination?.address || t('support.destination')} (₹{defaultRide.fare || 0})
                </div>
              </div>
            </div>
          )}

          <div className="support-section">
            <div className="support-section-label">{t('support.selectCategory')}</div>
            <div className="support-categories-grid">
              {ISSUE_CATEGORIES.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  className={`support-cat-pill ${selectedCategory === cat.id ? 'selected' : ''}`}
                  onClick={() => setSelectedCategory(cat.id)}
                >
                  {cat.emoji} {t(cat.tk)}
                </button>
              ))}
            </div>
          </div>

          <div className="support-section">
            <div className="support-section-label">{t('support.describe')}</div>
            <textarea
              className="support-notes-input"
              rows={2}
              placeholder={t('support.notesPh')}
              value={customNotes}
              onChange={(e) => setCustomNotes(e.target.value)}
            />
            <p className="support-reply-hint" style={{ marginTop: 8 }}>{t('support.attachmentHint') || 'If you have a screenshot or photo, attach it after WhatsApp opens.'}</p>
          </div>
        </div>

        <div className="support-modal-footer">
          <button type="button" className="btn btn-primary support-wa-btn" onClick={handleLaunchWhatsapp} disabled={launching}>
            💬 {launching ? (t('support.opening') || 'Opening…') : t('support.continueWhatsapp')}
          </button>
          <small className="support-reply-hint">{t('support.replyHint')}</small>
        </div>
      </div>
    </div>
  );
}
